import React, { useEffect, useRef, useState } from "react";
import Wrapper from "../../components/wrapper";
import StepPagination from "../../components/stepPagination";
import { useTranslation } from "react-i18next";
import { Button, Col, Row } from "react-bootstrap";
import { Formik, Form as FormikForm, FormikHelpers, FormikProps } from "formik";
import { IFirstProps } from "../first";
import {
  IEntity,
  PaymentsTicketOfficeTypeEnum,
  PaymentTypeEnum,
  TicketTypeEnum,
} from "../../types";
import {
  bankConnectSchema,
  bankDirectSchema,
  validationSchemaStep3,
} from "../../validations";
import PaymentTypes from "./paymentTypes";
import { useNavigate, useParams } from "react-router-dom";
import BankDetailsModal from "./paymentTypes/modal/BankDetailsModal";
import PaymentConnect from "./bankAccauntInfo/PaymentConnect";
import PaymentDirect from "./bankAccauntInfo/PaymentDirect";
import {
  useCreateBankAccountMutation,
  useCreateOrUpdatePaymentStripeAlternativeMutation,
  useCreateOrUpdatePaymentTicketOfficeMutation,
  useDeleteTicketOfficeMutation,
  useGetEventPaymentsQuery,
  useGetOrganizerServiceInfoQuery,
} from "../../qraphql/event.hooks";
import {
  BankAccountTypeInput,
  PaymentStripeAlternativeTypeInput,
} from "../../../graphql/types";
import ApolloError from "../../components/apolloError";
import ScrollToFieldError from "../../components/ScrollToFieldError";
import useGetUrlLocale from "../../hooks/useGetUrlLocale";
import useGetEventData from "../../hooks/useGetEventData";
import EventLoadingWrapper from "../../components/wrapper/EventLoadingWrapper";
import ErrorEventLoadingWrapper from "../../components/wrapper/ErrorEventLoadingWrapper";

export default function Second({
  entity,
  handleUpdateEntity,
  step,
}: IFirstProps) {
  const { t } = useTranslation();
  const formRef = useRef<FormikProps<IEntity>>(null);
  const navigate = useNavigate();
  const urlLocale = useGetUrlLocale();
  const [
    createOrUpdatePaymentStripeAlternative,
    {
      loading: loadingCreateOrUpdatePaymentStripeAlternative,
      error: createPaymentStripeAlternativeError,
    },
  ] = useCreateOrUpdatePaymentStripeAlternativeMutation();
  const [
    createBankAccount,
    { data: createBankAccountData, error: createBankAccountError },
  ] = useCreateBankAccountMutation();
  const [
    createOrUpdatePaymentTicketOffice,
    {
      data: createPaymentTicketOfficeData,
      error: createPaymentTicketOfficeError,
    },
  ] = useCreateOrUpdatePaymentTicketOfficeMutation();
  const [deleteTicketOffice, { error: deleteTicketOfficeError }] =
    useDeleteTicketOfficeMutation();
  const {
    data: eventPaymentsData,
    loading: eventPaymentsLoading,
    refetch: refetchEventPayments,
  } = useGetEventPaymentsQuery({
    variables: { eventId: entity.id },
  });
  const { data } = useGetOrganizerServiceInfoQuery();
  const { getEventData, isEventLoading, eventError } = useGetEventData({
    handleUpdateEntity,
    step: 2,
  });

  const eventPayment = eventPaymentsData?.eventProxy.payments[0];

  const [showBankAccountInfo, setShowBankAccountInfo] = useState(false);
  const [isBankDetailsModal, setIsBankDetailsModal] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);

  const { id, eventId } = useParams();

  useEffect(() => {
    getEventData();
  }, []);

  const handleShowBankAccоuntInfo = (isShow: boolean) => {
    setShowBankAccountInfo(isShow);
  };

  const onSubmit = async (
    values: IEntity,
    { setSubmitting }: FormikHelpers<IEntity>
  ) => {
    if (showBankAccountInfo) {
      // check if modal stripe alternative confirmation open
      setIsBankDetailsModal(true);
    } else if (eventId) {
      // check if create stripe bank account mode
      const bankConnectValues = values.bankConnect;
      const input: BankAccountTypeInput = {
        countryCode: bankConnectValues.selectedStripeCountryCode,
        currencyName: bankConnectValues.currency,
        holderName: bankConnectValues.holderName,
        iban: undefined,
        routing: undefined,
      };

      if (bankConnectValues.isIban) {
        input.iban = bankConnectValues.iban;
      } else if (bankConnectValues.isNumber) {
        input.iban = bankConnectValues.accountNumber;
      }

      if (bankConnectValues.isNumber) {
        input.routing = bankConnectValues.accountNumber;
      } else if (bankConnectValues.isRouting) {
        input.routing = bankConnectValues.routingNumber;
      }

      try {
        await createBankAccount({
          variables: {
            eventId: Number(eventId),
            input,
          },
        });
        setSubmitting(false);
      } catch (error) {
        setSubmitting(false);
      }
    } else if (!values.isPayOnline) {
      // check if payment is ticket office
      try {
        await createOrUpdatePaymentTicketOffice({
          variables: {
            eventId: values.id,
            input: {
              address: values.ticketAddresses,
              email: values.merchantContacts.map((item) => item.email),
              phone: values.merchantContacts.map((item) => `+${item.phone}`),
            },
            ticketOfficeType: values.ticketOfficeType,
          },
        });
        setSubmitting(false);
      } catch (error) {
        setSubmitting(false);
      }
    } else if (
      // check if payment is stripe_alternative or stripe
      values.isPayOnline &&
      (eventPayment?.type === PaymentTypeEnum.StripeAlternative ||
        eventPayment?.stripeConnectBankAccountId)
    ) {
      navigate(`${urlLocale}/check-publish`);
      await handleUpdateEntity(values, 3);
    }
  };

  useEffect(() => {
    if (createPaymentTicketOfficeData || createBankAccountData) {
      handleUpdateEntity(formRef.current.values, 3);
      navigate(`${urlLocale}/check-publish`);
    }
    // eslint-disable-next-line
  }, [createPaymentTicketOfficeData, createBankAccountData]);

  const handleCloseBankDetailsInfo = () => {
    setIsBankDetailsModal(false);
  };

  const handleSaveBankDetailsInfo = async () => {
    const values = formRef.current.values.bankDirect;
    const inputInfo: PaymentStripeAlternativeTypeInput = {
      email: values.email,
      phone: `+${values.phone}`,
      address: values.address,
      city: values.city,
      postalCode: Number(values.postalCode),
      countryId: Number(values.countryAccount),
      countryOfTheBankAccountId: Number(values.countryResidence),
      nameOfTheBank: values.bankName,
      accountHolderName: values.holderName,
      iban: values.iban,
      swiftOrBic: values.swiftBic,
      accountNumber: values.accountNumber,
      feePayerType: values.isIncludeFee,
    };
    try {
      await createOrUpdatePaymentStripeAlternative({
        variables: {
          eventId: formRef.current.values.id,
          input: inputInfo,
        },
      });
      setShowBankAccountInfo(false);
      await handleUpdateEntity(formRef.current.values, 2);
    } catch (error) {
    } finally {
      handleCloseBankDetailsInfo();
    }
    await refetchEventPayments();
  };

  const nextStep = async () => {
    if (formRef.current) {
      if (eventPayment?.type === PaymentTypeEnum.Cash) {
        await deleteTicketOffice({
          variables: { eventId: formRef.current.values.id },
        });
      }
      navigate(`${urlLocale}/check-publish`);
      handleUpdateEntity(formRef.current.values, 3);
    }
  };

  const handlePaymentConnectLoading = (isLoading: boolean) => {
    setIsDataLoading(isLoading);
  };

  if (isEventLoading) {
    return (
      <EventLoadingWrapper
        step={step}
        title={t("firstStep.createEvent")}
        stepName={t("thirdStep.paymnetTypes")}
      />
    );
  }

  if (eventError) {
    return (
      <ErrorEventLoadingWrapper
        step={step}
        title={t("firstStep.createEvent")}
        errorLoadImageFiles={false}
        eventError={eventError}
      />
    );
  }

  return (
    <Wrapper>
      {isBankDetailsModal && (
        <BankDetailsModal
          handleClose={handleCloseBankDetailsInfo}
          handleSave={handleSaveBankDetailsInfo}
          loading={loadingCreateOrUpdatePaymentStripeAlternative}
        />
      )}
      <h1 className="fw-bold fs-1 mb-4 mb-md-5">
        {t("firstStep.createEvent")}
      </h1>
      <StepPagination step={step} />
      <Formik
        innerRef={formRef}
        initialValues={entity}
        onSubmit={onSubmit}
        validationSchema={
          eventId
            ? bankConnectSchema(t)
            : showBankAccountInfo
              ? bankDirectSchema(t)
              : validationSchemaStep3(t)
        }
        enableReinitialize
      >
        {({ isSubmitting, isValid, submitCount, values }) => {
          const shouldShowSubmitButton =
            (values.isPayOnline &&
              (eventPayment?.type === PaymentTypeEnum.StripeAlternative ||
                eventPayment?.stripeConnectBankAccountId)) ||
            !values.isPayOnline ||
            showBankAccountInfo ||
            eventId;
          const isSkipStep = values.tickets.every(
            (ticket) =>
              (ticket.ticketType === TicketTypeEnum.Free &&
                ticket.active === true) ||
              (ticket.ticketType !== TicketTypeEnum.Free &&
                ticket.active === false)
          );
          const shouldShowSkipButton =
            isSkipStep &&
            values.isPayOnline &&
            !showBankAccountInfo &&
            !eventId &&
            !eventPayment?.stripeConnectBankAccountId &&
            eventPayment?.type !== PaymentTypeEnum.StripeAlternative;

          const totalTicketsQuantity = values.tickets
            .filter((item) => item.active)
            .reduce((acc, item) => acc + item.quantity, 0);
          const isFreeTicketsQuantityExceed =
            !data?.activeSubscription &&
            values.ticketOfficeType ===
              PaymentsTicketOfficeTypeEnum.GenerateTicketTicketOfficeType &&
            totalTicketsQuantity > 50;

          return (
            <FormikForm>
              <ScrollToFieldError />
              {eventId ? (
                <PaymentConnect
                  handlePaymentConnectLoading={handlePaymentConnectLoading}
                  handleUpdateEntity={handleUpdateEntity}
                />
              ) : showBankAccountInfo ? (
                <PaymentDirect />
              ) : (
                <PaymentTypes
                  handleShowBankAccоuntInfo={handleShowBankAccоuntInfo}
                  eventPayment={eventPayment}
                  handleUpdateEntity={handleUpdateEntity}
                  eventPaymentsLoading={eventPaymentsLoading}
                  isFreeTicketsQuantityExceed={isFreeTicketsQuantityExceed}
                />
              )}

              <Row className="justify-content-between my-5">
                <Col xs={4} lg={2}>
                  <Button
                    variant="outline-success"
                    className="w-100 py-3 fw-normal"
                    onClick={() => {
                      handleUpdateEntity(entity, 1);
                      navigate(
                        `${urlLocale}/tickets-info/${id || values.id}/edit`
                      );
                    }}
                  >
                    {t("secondStep.back")}
                  </Button>
                </Col>
                {shouldShowSubmitButton && (
                  <Col xs={8} lg={4}>
                    <Button
                      variant="success"
                      className="fw-bold w-100 py-3"
                      type="submit"
                      disabled={
                        isDataLoading ||
                        isSubmitting ||
                        (submitCount > 0 && !isValid) ||
                        (isFreeTicketsQuantityExceed && !values.isPayOnline)
                      }
                    >
                      {t("firstStep.next")}
                    </Button>
                  </Col>
                )}
                {shouldShowSkipButton && (
                  <Col xs={8} lg={4}>
                    <Button
                      variant="success"
                      className="fw-bold w-100 py-3"
                      onClick={nextStep}
                    >
                      {t("firstStep.next")}
                    </Button>
                  </Col>
                )}
              </Row>
            </FormikForm>
          );
        }}
      </Formik>
      <ApolloError networkError={createBankAccountError?.networkError} />
      <ApolloError
        networkError={createPaymentStripeAlternativeError?.networkError}
      />
      <ApolloError
        networkError={createPaymentTicketOfficeError?.networkError}
      />
      <ApolloError networkError={deleteTicketOfficeError?.networkError} />
    </Wrapper>
  );
}
